import React, {
  createContext,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDeviceType } from 'b2b-apps/shop/common/hooks';
import { isMobile } from 'b2b-apps/shop/common/utils/env';

type StickyHeaderContextValue = {
  isEnabled: boolean;
  bottom: number;
  ref: Ref<HTMLDivElement>;
}

const StickyHeaderContext = createContext<StickyHeaderContextValue | undefined>(undefined);

type StickyHeaderProviderProps = {
  children: ReactNode;
}

const DEFAULT_STICKY_HEIGHT = 144;

const StickyHeaderProvider = ({ children }: StickyHeaderProviderProps): ReactElement => {
  const deviceType = useDeviceType();
  const [isEnabled, setIsEnabled] = useState(!isMobile(deviceType));
  const ref = useRef<HTMLDivElement | null>(null);
  const prevScrollY = useRef<number>(0);
  const bottom = isEnabled ? ref?.current?.clientHeight || DEFAULT_STICKY_HEIGHT : 0;

  const handleScroll = useCallback(
    (): void => {
      setIsEnabled(prevScrollY.current > window.pageYOffset);
      prevScrollY.current = window.pageYOffset;
    },
    [],
  );

  useEffect(
    (): () => void => {
      if (isMobile(deviceType)) {
        window.addEventListener('scroll', handleScroll);
      }

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    },
    [deviceType, handleScroll],
  );

  return (
    <StickyHeaderContext.Provider value={{
      isEnabled,
      bottom,
      ref,
    }}>
      {children}
    </StickyHeaderContext.Provider>
  );
};

const useStickyHeader = (): StickyHeaderContextValue => {
  const context = useContext(StickyHeaderContext);

  if (context === undefined) {
    throw new Error('useStickyHeader must be used within a DifferencesHighlightProvider');
  }

  return context;
};

export { StickyHeaderProvider, useStickyHeader };
